import { create } from 'zustand';
import { CallcenterStore, CallcenterState, IntakeConflict } from './types';
import { CallcenterWizardData, FoerderanspruchResult, ContactSearchReactivation } from '@/types/callcenter';
import { berechneGesamtFoerderanspruch } from '@/services/foerderrechner';
import { buildRouteLeadPayload } from './buildRouteLeadPayload';
import { LETZTER_WIZARD_SCHRITT, klemmeWizardSchritt } from './wizardSchritte';
import { buildWizardCustomerPayload } from './buildWizardCustomerPayload';
import { buildWizardDataFromCustomer } from './buildWizardDataFromCustomer';
import {
  ladeWizardSitzung,
  speichereWizardSitzung,
  vergissWizardSitzung,
  istSpeicherwuerdig,
  WizardSitzung,
} from './wizardSitzung';
import api from '@/services/api';
import useAuthStore from '@/store/auth';
import { ErrorResponse } from '@/utils';
import { ersteValidierungsMeldung } from '@/utils/error';
import { absichtSchluessel, absichtAbschliessen } from '@/utils/absendeAbsicht';
import { AxiosError } from 'axios';

const initialWizardData: CallcenterWizardData = {
  haushaltssituation: '',
  berufsstand1: '',
  berufsbezeichnung: '',
  geburtsjahr: null,
  bruttogehalt1: null,
  nettogehalt1: null,
  einkuenfte: 'keine',
  einkuenfteHoehe: null,
  kinder: 0,
  kinderImHaushalt: 0,
  kinderGeburtsjahre: [],
  plz: '',
  ort: '',
  // Brueckenfelder fuer den heutigen foerderrechner — UI entfernt
  // (FE-Verschlankung 2026-06-09). Cleanup mit Foerderrechner-Rebuild.
  kirchensteuer: 'nein',
  berufsstand2: '',
  berufsbezeichnungPartner: '',
  geburtsjahrPartner: null,
  bruttogehalt2: null,
  nettogehalt2: null,
  einkuenfte_partner: 'keine',
  einkuenfteHoehe_partner: null,
  kirchensteuerPartner: 'nein',
  // Kein Default — User muss im Wizard aktiv Ja/Nein waehlen (Anforderung
  // Mirkin 20.06.2026 fuer immobilie; 04.07.2026 auf Arbeitskraftabsicherung
  // + Sparvertraege ausgeweitet). Step2/3/4 haben je bereits required + Select-
  // Error, ein leerer Default blockt daher das Step-Gate bis zur aktiven Wahl.
  immobilie: '',
  arbeitskraftabsicherung: '',
  krankenkasse: '',
  krankenkassePartner: '',
  sparvertraege: '',
  sparvertraege_daten: [],
  sparvertraegeKinder: 'nein',
  sparvertraegeKinder_daten: [],
  anrede: '',
  vorname: '',
  nachname: '',
  telefonnummer: '',
  email: '',
  anredePartner: '',
  vornamePartner: '',
  nachnamePartner: '',
  leadherkunft: '',
  device: '',
  notizen: '',
};

const emptyFoerderanspruch: FoerderanspruchResult = {
  total: 0,
  foerderhoehe: 0,
  items: [],
  kirchensteuerData: null,
  kirchensteuerPartnerData: null,
};

const initialState: CallcenterState = {
  currentStep: 0,
  wizardData: { ...initialWizardData },
  foerderanspruch: { ...emptyFoerderanspruch },
  routedPool: null,
  rcAuffangnetz: null,
  routedLoading: false,
  routedError: null,
  routedFehlerMeldung: null,
  // KEIN stiller Default mehr (Auftrag Alex 07.08.2026). `null` = noch nicht
  // gewaehlt; der Wizard erzwingt die Wahl, bevor es weitergeht. Herleitung und
  // der Vorfall dahinter (Meeting 54415) stehen in `./buchungsmodus.ts`.
  bookingType: null,
  modusWahlOffen: false,
  availableSlots: [],
  slotRejection: null,
  slotDisplayContext: null,
  bookingResult: null,
  dataSaved: false,
  loading: false,
  error: null,
  intakeConflict: null,
  existingCustomerId: null,
  existingCustomerNextMeeting: null,
  existingCustomerName: '',
  searchPerformed: false,
  healthInsurances: [],
  matchReasons: [],
  searchPath: null,
  searchBlock: null,
  searchOpenCase: null,
  searchCandidates: [],
  activeOpenCase: null,
  recyclingCustomerId: null,
  searchReactivation: null,
  activeRecycling: null,
  testMode: false,
  testRunSaved: false,
  practiceModeAvailable: null,
};

/**
 * Buchungs-Fehlercode (error.code aus der Domain-Exception) auslesen.
 * Undefined, wenn kein strukturierter Code vorliegt (z. B. Validierungsfehler).
 */
export function getBookingErrorCode(error: unknown): string | undefined {
  const axiosErr = (error as { error: AxiosError }).error;
  const data = axiosErr?.response?.data as { error?: { code?: string } } | undefined;
  return data?.error?.code;
}

// SLOT_TAKEN-Härtung Stufe 1 (08.07.): Codes, bei denen sich der angezeigte
// Stand geändert haben kann → Slot-Liste automatisch neu laden + Auswahl
// zurücksetzen (Auto-Refresh, KEIN Auto-Retry). docs/tasks/phase-0/slottaken-haertung.md
export const SLOT_REFRESH_CODES = new Set([
  'SLOT_TAKEN',
  'SLOT_BUFFER_BLOCKED',
  'SLOT_FORWARD_BUFFER_BLOCKED',
  'SLOT_OUTSIDE_RASTER',
  'MIN_NOTICE_BLOCKED',
  'NO_SPECIALIST_AVAILABLE',
  'SPECIALIST_UNAVAILABLE',
  'CALENDAR_UNAVAILABLE',
  'POOL_LOCK_TIMEOUT',
  // Entscheid Alex 06.08.2026: die angezeigte Liste kam aus einem anderen
  // Pool — sie MUSS weg, sonst klickt der Agent denselben Slot erneut.
  'POOL_MISMATCH',
  // Decision #238: Zeit außerhalb der Buchbaren Zeiten des Pools (z. B. nach
  // einer Änderung der Zeiten) — Liste neu laden, die Zeit ist nicht mehr dabei.
  'AUSSERHALB_SERVICEZEITEN',
]);

/**
 * Liest die Fehlermeldung aus einem Buchungs-Fehler heraus.
 *
 * Booking-Domain-Exceptions liefern { error: { code, message } } — daher reicht
 * ErrorResponse.getResponseMessage() (Pfad data.message) nicht.
 *
 * SLOT_TAKEN-Härtung 08.07.: die vier Verfügbarkeits-Codes werden getrennt
 * gemeldet (vorher eine Sammelmeldung), damit der Agent Ursache + Handlung
 * unterscheiden kann.
 */
export function extractBookingError(error: unknown): string {
  const axiosErr = (error as { error: AxiosError }).error;
  const data = axiosErr?.response?.data as
    | { message?: string; error?: { code?: string; message?: string }; errors?: Record<string, string[]> }
    | undefined;

  const code = data?.error?.code;
  if (code === 'GOOGLE_CALENDAR_REQUIRED') {
    // Neutral statt Selbstbuchungs-Text (Alex 04.08.2026).
    //
    // Der bisherige Text ("Du hast noch kein Google-Konto verbunden … bevor du
    // fuer dich selbst buchst") gilt nur fuer den Self-Pfad. Diese Funktion
    // faengt aber BEIDE Zweige von submitAndBook ab — auch die Pool-Buchung,
    // bei der es NICHT um das eigene Konto geht, sondern um das des vom WRR
    // gewaehlten Beraters. Ein Telefonist (erst recht ein gebundener) las dort
    // eine Aufforderung, etwas an seinen eigenen Einstellungen zu aendern, was
    // den Fehler nicht behebt.
    //
    // Das Backend liefert bereits die passende Meldung pro Pfad
    // (`error.message`, durchgereicht in bootstrap/app.php): Self-Gate nennt das
    // eigene Konto, der zentrale Guard nennt den ausgewaehlten Berater. Wir
    // uebernehmen sie deshalb, statt sie zu ueberschreiben — der Fallback bleibt
    // bewusst neutral formuliert, ohne Annahme darueber, fuer wen gebucht wird.
    return (
      data?.error?.message
      ?? 'Es fehlt eine verknuepfte Google-Kalender-Verbindung. Bitte Google Calendar verbinden bzw. den Admin kontaktieren.'
    );
  }
  if (code === 'SLOT_TAKEN') {
    // Neutral seit dem 14.09.2026 (Entscheid Alex, Nachschub 15.09.): SLOT_TAKEN
    // heisst nur „im Spiegel belegt", nicht „ein anderer war schneller" —
    // gleichlautend mit dem BE-Text und dem Fall-Dialog.
    return 'Der Slot ist im Kalender belegt und nicht mehr verfügbar. Bitte anderen Slot wählen.';
  }
  // Entscheid Alex 06.08.2026: NICHT mehr „inzwischen vergeben". Der Slot ist
  // in diesem Fall gar nicht vergeben — im Pool ist nur kein Berater frei.
  // Der alte, mit SLOT_TAKEN geteilte Satz war am 06.08. die Meldung, die eine
  // gebundene Telefonistin sah, und er hat die Ursachensuche stundenlang auf
  // die SLOT_TAKEN-Faehrte geschickt, wo nichts zu finden war.
  if (code === 'NO_SPECIALIST_AVAILABLE') {
    return 'Aktuell kein Berater verfügbar — bitte einen anderen Zeitpunkt wählen.';
  }
  // Riegel gegen Anzeige-/Buchungs-Drift (Entscheid Alex 06.08.2026), seit
  // 10.09.2026 eine echte NACHRECHNUNG des Pools aus dem Buchungs-Payload
  // (Entscheid Alex, Case 33853): die Einstufung aus den erfassten Daten passt
  // nicht zu dem Pool, aus dem die angezeigten Slots stammen. Die BE-Meldung
  // sagt dasselbe — wir nehmen sie, wenn sie da ist, damit Wortlaut-Aenderungen
  // nicht an zwei Stellen gepflegt werden; der Fallback ist nur die Notbremse
  // und wurde am 10.09. mitgezogen, damit beide Seiten dasselbe sagen.
  if (code === 'POOL_MISMATCH') {
    return (
      data?.error?.message
      ?? 'Die Einstufung hat sich geändert — bitte neu prüfen.'
    );
  }
  if (code === 'SPECIALIST_UNAVAILABLE' || code === 'CALENDAR_UNAVAILABLE') {
    return 'Die Kalenderprüfung ist fehlgeschlagen — bitte kurz warten und erneut versuchen.';
  }
  if (code === 'POOL_LOCK_TIMEOUT') {
    return 'Das System ist gerade ausgelastet — bitte in wenigen Sekunden erneut versuchen.';
  }

  // Laravel-Validierungsfehler (422 mit errors-Objekt): Feldbezug anzeigen
  if (data?.errors && Object.keys(data.errors).length > 0) {
    return Object.entries(data.errors)
      .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
      .join(' — ');
  }

  return data?.error?.message
    ?? data?.message
    ?? (error as ErrorResponse).getResponseMessage?.()
    ?? 'Fehler beim Buchen';
}

/** ISO-Datum → „TT.MM.JJJJ"; leer/ungueltig → null (nie „Invalid Date"). */
function datumDe(wert: unknown): string | null {
  if (typeof wert !== 'string' || wert === '') return null;
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('de-DE');
}

/** Erster nicht-leerer Text aus den Kandidaten, sonst null. */
function ersterText(...werte: unknown[]): string | null {
  const treffer = werte.find((w) => typeof w === 'string' && w.trim() !== '');
  return (treffer as string | undefined) ?? null;
}

/** Erste ganze Zahl aus den Kandidaten, sonst null. */
function ersteId(...werte: unknown[]): number | null {
  const treffer = werte.find((w) => typeof w === 'number' && Number.isInteger(w));
  return (treffer as number | undefined) ?? null;
}

/**
 * Prueft ob ein Fehler ein Intake-Konflikt (409) ist.
 * ErrorResponse wickelt den AxiosError — wir greifen auf die innere Struktur zu.
 *
 * WV-110 (b): am Submit kommt die 409 fast immer vom Buchungs-Waechter
 * (`BookingBlockedException::details()`: `vp_name`, `scheduled_at`, `case_id`,
 * `customer_ids`, `source` — ohne Thema und Datum). Die Intake-Form (`id`,
 * `topic_name`, `created_at` bzw. `assigned_vp_name`, `closed_at`) kommt nur,
 * wenn der Waechter durchliess. Beide Formen werden gelesen; fehlt Thema oder
 * Datum, steht ein Satz ohne sie da, nie ein leeres „Topic: .".
 */
export function extractIntakeConflict(error: unknown): IntakeConflict | null {
  // ErrorResponse hat getStatus() und internen AxiosError
  const errResp = error as ErrorResponse;
  if (errResp?.getStatus?.() !== 409) return null;

  // Zugriff auf response.data via den inneren AxiosError
  const axiosErr = (error as { error: AxiosError }).error;
  const data = axiosErr?.response?.data as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | undefined;
  const code = data?.error?.code;

  if (code === 'OPEN_CASE_EXISTS') {
    const details = data?.error?.details;
    const vpName = ersterText(details?.vp_name);
    const topicName = ersterText(details?.topic_name);
    const erstellt = datumDe(details?.created_at);
    const casesCount = (details?.existing_cases_count as number) ?? 1;
    // Waechter: `case_id`; Intake: `id`.
    const existingCaseId = ersteId(details?.case_id, details?.id);
    const message = casesCount > 1
      ? (topicName
        ? `Es existiert mindestens ein offener Case zum Topic ${topicName}. Buchung nicht moeglich.`
        : 'Es existieren bereits offene Cases zu diesem Kunden. Buchung nicht moeglich.')
      : [
        vpName ? `Offener Case vorhanden bei VP ${vpName}.` : 'Offener Case vorhanden.',
        topicName ? `Topic: ${topicName}.` : null,
        erstellt ? `Erstellt am: ${erstellt}.` : null,
        'Buchung nicht moeglich.',
      ].filter(Boolean).join(' ');
    return {
      type: 'open_case_exists',
      message,
      details,
      existingCasesCount: casesCount,
      existingCaseId,
    };
  }
  if (code === 'WON_CASE_EXISTS_WITH_ACTIVE_VP') {
    const details = data?.error?.details;
    // Waechter: `vp_name`; Intake: `assigned_vp_name`.
    const vpName = ersterText(details?.vp_name, details?.assigned_vp_name);
    const topicName = ersterText(details?.topic_name);
    const geschlossen = datumDe(details?.closed_at);
    const existingCaseId = ersteId(details?.case_id);
    const fall = topicName ? `Gewonnener ${topicName}-Case` : 'Gewonnener Case';
    const message = [
      vpName ? `Der Kunde ist bereits Bestandskunde bei VP ${vpName}.` : 'Der Kunde ist bereits Bestandskunde.',
      geschlossen ? `${fall} vom ${geschlossen}.` : (topicName ? `${fall}.` : null),
      'Buchung nicht moeglich, solange der VP aktiv ist.',
    ].filter(Boolean).join(' ');
    return {
      type: 'won_case_exists',
      message,
      details,
      existingCaseId,
    };
  }
  // Buchungs-Waechter 22.07. (Alex): der Submit weist jetzt serverseitig ab.
  // Ohne diese Zweige landete jeder Waechter-Block im Sammel-Fallback und der
  // Agent saehe "Buchung fehlgeschlagen" statt des echten Grundes.
  // Seit Decision #181 (28.08.2026) gibt es nur noch den BW-Code: der
  // Buchungs-Waechter fragt HubSpot nicht mehr, deshalb faellt auch der
  // Quellen-Zusatz „(HubSpot)" an der Meldung weg.
  if (code === 'UPCOMING_MEETING_EXISTS') {
    const details = data?.error?.details;
    const vpName = (details?.vp_name as string) ?? 'unbekannt';
    const at = (details?.scheduled_at as string) ?? '';
    const wann = at ? new Date(at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : 'unbekannt';
    return {
      type: 'upcoming_meeting_exists',
      message: `Der Kunde hat bereits einen Termin am ${wann} bei ${vpName}. Buchung nicht moeglich.`,
      details,
    };
  }
  if (code === 'EMAIL_MATCH_BLOCKED') {
    const details = data?.error?.details;
    return {
      type: 'email_match_blocked',
      message: `Diese E-Mail gehoert zu einem Kunden, der bereits betreut wird (${(details?.vp_name as string) ?? 'unbekannt'}). Buchung nicht moeglich.`,
      details,
    };
  }
  if (code === 'AMBIGUOUS_CUSTOMER_MATCH') {
    return {
      type: 'ambiguous_customer_match',
      message: 'Telefonnummer und E-Mail stimmen mit unterschiedlichen Kunden ueberein. Bitte Daten pruefen oder beim SA melden.',
      details: data?.error?.details,
    };
  }
  return null;
}

const useCallcenterStore = create<CallcenterStore>((set, get) => ({
  ...initialState,

  setStep: (step: number) => set({ currentStep: klemmeWizardSchritt(step) }),
  nextStep: () => set((s) => ({ currentStep: Math.min(s.currentStep + 1, LETZTER_WIZARD_SCHRITT) })),
  prevStep: () => set((s) => ({ currentStep: Math.max(s.currentStep - 1, 0) })),

  updateWizardData: (partial) => {
    set((s) => ({ wizardData: { ...s.wizardData, ...partial } }));
    get().recalculateFoerderanspruch();
  },

  recalculateFoerderanspruch: () => {
    const result = berechneGesamtFoerderanspruch(get().wizardData);
    set({ foerderanspruch: result });
  },

  searchCustomer: async (email?: string, phone?: string) => {
    try {
      set({
        loading: true,
        error: null,
      });
      const result = await api.callcenter.searchCustomerByContact(email, phone);

      if (result.matched && result.customer) {
        const c = result.customer;

        // Rueck-Builder: Customer → Partial<CallcenterWizardData>.
        // Single Source der Hydration, Gegenstueck zu
        // buildWizardCustomerPayload. Liest Partner aus partner_data
        // (JSONB, Decision #69), nicht aus der c.partner-Relation.
        const partial = buildWizardDataFromCustomer(c);

        set((s) => ({
          wizardData: { ...s.wizardData, ...partial },
          existingCustomerId: c.id,
          existingCustomerNextMeeting: result.next_meeting ?? null,
          existingCustomerName: `${c.first_name} ${c.last_name}`,
          // Fallback ?? [] symmetrisch zum not-matched-Zweig (:243). Fehlt
          // match_reasons in der Response-Shape (Bundle-Skew/abweichende Antwort),
          // wäre matchReasons sonst undefined → matchReasons.includes() wirft im
          // ContactSearch-Render (reminderFeed.map-Muster). Kein Verhaltens-
          // unterschied bei korrekter Shape. Siehe docs/bugs/2026-07-08-telefonie-bereich-ladefehler.md.
          matchReasons: result.match_reasons ?? [],
          searchPath: result.path ?? null,
          searchBlock: result.block ?? null,
          searchOpenCase: result.open_case ?? null,
          searchCandidates: result.candidates ?? [],
          searchReactivation: result.reactivation ?? null,
          // 🔥 DIE ANZEIGE FOLGT DEM PIN (Decision #168, Alex 18.08.2026).
          //
          // `activeRecycling` steuert ueber RoutedPoolDisplay den
          // `pinnedVpId` des Kalenders — die Slot-Anzeige zeigt dann
          // AUSSCHLIESSLICH die Zeiten des gepinnten Alt-VP. Genau das fehlte
          // seit #152: der Server pinnt (seit #192, R1), die Anzeige zeigte
          // weiter den Pool, und der Agent klickte Slots, die der gepinnte
          // Berater gar nicht frei hatte.
          //
          // ⚠️ BEWUSST NICHT ueber `startRecyclingBooking()` — das setzt
          // zusaetzlich `currentStep` auf die Zusammenfassung und ist der alte Express-Direktsprung
          // aus Stufe 3. Den hat #152 abgeschafft, und er kommt hier NICHT
          // zurueck: der Agent laeuft den normalen Wizard, nur der Kalender
          // zeigt am Ende den richtigen Berater. `recyclingCustomerId` bleibt
          // ebenfalls ungesetzt — seit #166 R4 entscheidet `customer_id`
          // nichts mehr, die Weiche haengt serverseitig am Customer-Match.
          activeRecycling: result.reactivation ?? null,
          // Neue Suche = neuer Kunde = altes Weichen-Urteil ungueltig
          // (Decision #171). Es entsteht erst mit der naechsten Pool-Vorschau
          // neu; bis dahin darf kein Banner aus einem fremden Vorgang stehen.
          rcAuffangnetz: null,
        }));
        get().recalculateFoerderanspruch();
      } else {
        set({
          existingCustomerId: null,
          existingCustomerNextMeeting: null,
          existingCustomerName: '',
          matchReasons: result.match_reasons ?? [],
          searchPath: result.path ?? null,
          searchBlock: result.block ?? null,
          searchOpenCase: result.open_case ?? null,
          searchCandidates: result.candidates ?? [],
          searchReactivation: result.reactivation ?? null,
          // Unbekannte Nummer = Neukunde = niemals ein Pin. Explizit leeren
          // statt sich auf den Reset zu verlassen: sucht ein Agent nach einem
          // RC-Kunden und danach nach einer unbekannten Nummer, stuende der
          // Pin des VORIGEN Kunden sonst noch im Store — und der Kalender
          // zeigte fuer einen Neukunden die Slots eines fremden Beraters.
          activeRecycling: null,
          rcAuffangnetz: null,
        });
      }
    } catch (error) {
      set({ error: (error as ErrorResponse).getResponseMessage?.() ?? 'Fehler bei der Kontaktsuche' });
    } finally {
      set({ loading: false });
    }
  },

  // Vorne in der ContactSearch eingegebene E-Mail/Telefon werden in den
  // Wizard uebernommen, damit Step5Interessent sie nicht erneut abfragt.
  // Beide Felder bleiben editierbar (Formik enableReinitialize liest aus
  // wizardData, onChange schreibt zurueck).
  startNewCustomer: (email?: string, phone?: string) => set({
    searchPerformed: true,
    wizardData: {
      ...initialWizardData,
      email: email ?? '',
      telefonnummer: phone ?? '',
    },
    existingCustomerId: null,
    existingCustomerNextMeeting: null,
    existingCustomerName: '',
    foerderanspruch: { ...emptyFoerderanspruch },
    matchReasons: [],
    searchPath: null,
    searchBlock: null,
      searchOpenCase: null,
    searchCandidates: [],
    activeOpenCase: null,
    recyclingCustomerId: null,
    searchReactivation: null,
    activeRecycling: null,
    rcAuffangnetz: null,
  }),

  // Pfad B und E teilen sich diesen Button. Bei E wird der offene Case gepinnt:
  // ohne den Pin bucht der Wizard über Pool/WRR und der Submit-Intake weist den
  // offenen Case mit 409 ab (OpenCaseConflictPolicy Regel 2).
  continueWithCustomer: () => set((s) => ({
    searchPerformed: true,
    activeOpenCase: s.searchOpenCase,
  })),

  // Regel 8 (Auftrag Alex 22.07.): der Agent waehlt aus der Telefon-Gruppe.
  // Nur die Auswahl umsetzen, NICHT weiterspringen — der Block-Verdict gilt
  // fuer die ganze Gruppe und muss weiter sichtbar bleiben.
  selectCandidate: (customerId: number) => set((s) => {
    const k = s.searchCandidates.find((c) => c.id === customerId);

    return k === undefined ? {} : {
      existingCustomerId: k.id,
      existingCustomerName: k.name,
    };
  }),

  // 7.15/Stufe 3 Pfad C — Direktsprung zum Buchungs-Schritt (Zusammenfassung,
  // seit N1 01.10.2026 Index 5 — LETZTER_WIZARD_SCHRITT).
  // Pinnt customer_id (Submit → 2a Same-VP) und activeRecycling (Recycling-
  // Notice + Slot-Anzeige des gepinnten Vor-VP). Wizard-Daten kommen aus der
  // vorangehenden searchCustomer-Prefill, deshalb hier kein Daten-Reset.
  startRecyclingBooking: (reactivation: ContactSearchReactivation) => set({
    searchPerformed: true,
    currentStep: LETZTER_WIZARD_SCHRITT,
    recyclingCustomerId: reactivation.customer_id,
    activeRecycling: reactivation,
  }),

  /**
   * Decision #61: ruft POST /api/callcenter/route-lead, um Lead-Pool zu
   * klassifizieren. Pool wird im Wizard read-only angezeigt; assigned_user
   * aus der Antwort wird verworfen (finale VP-Allokation laeuft beim
   * Submit ueber bookMeeting/bookForMe).
   */
  routeLead: async () => {
    const { wizardData, bookingType } = get();

    // OHNE MODUS KEINE VORSCHAU (07.08.2026). Der Modus bestimmt seit der
    // Bindungs-Revision mit, welcher Pool herauskommt — eine Vorschau ohne ihn
    // waere geraten, und aus ihr speist sich die Slot-Anzeige. Der Fall tritt
    // im Wizard nicht auf (der Modus faellt am Start), aber ein geratener Pool
    // ist genau die Sorte stiller Annahme, aus der #146 entstanden ist.
    if (bookingType === null) {
      set({ routedPool: null, rcAuffangnetz: null, routedError: null, routedFehlerMeldung: null, routedLoading: false });

      return;
    }
    // Finale Alex-Entscheidung 2026-07-12: die Vorschau feuert wieder beim
    // Step7-Mount unabhängig von der Leadherkunft (kein Store-Guard mehr). Die
    // Leadherkunft ist NUR über das Step-5-Pflichtfeld erzwungen; Step5-über-
    // springende Pfade (Recycling/Bestandskunde) schicken sie leer → route-lead
    // ist BE-seitig nullable und routet dann nach Einkommen/Alter (normales
    // Routing wie vor dem Feature).
    try {
      set({ routedLoading: true, routedError: null, routedFehlerMeldung: null });
      // Live-Logik: Netto-Haushalt, Backend aggregiert die vier Felder. Der
      // Payload wird von buildRouteLeadPayload gebaut — dieselbe hatPartner-
      // Regel wie buildWizardCustomerPayload (Entscheid Alex 06.08.2026).
      // Vorher stand er hier inline und schickte die Partner-Felder
      // bedingungslos; das war die Rest-Divergenz nach Decision #146.
      const response = await api.callcenter.routeLead(buildRouteLeadPayload(wizardData, bookingType));
      set({
        routedPool: response.pool,
        // RC-Auffangnetz (Decision #171): das Urteil kommt aus dem Backend und
        // wird hier NUR uebernommen. Es blendet in `RoutedPoolDisplay` den Pin
        // aus und schaltet im Buchungs-Schritt das Weichen-Banner — beides
        // Folge dieser einen Antwort, keine eigene Rechnung.
        //
        // `?? null` und nicht weglassen: ein Aufrufer, der die Vorschau erneut
        // fahren laesst (Modus-Wechsel), muss ein FRUEHERES Urteil loswerden.
        // Bliebe es stehen, zeigte der Wizard das Banner eines Pools weiter,
        // in dem gar nicht mehr gebucht wird.
        rcAuffangnetz: response.rc_weiche ?? null,
      });
    } catch (error) {
      const message = (error as ErrorResponse).getResponseMessage?.() ?? 'Pool-Klassifikation fehlgeschlagen';
      set({ routedError: message, routedFehlerMeldung: ersteValidierungsMeldung(error), routedPool: null, rcAuffangnetz: null });
    } finally {
      set({ routedLoading: false });
    }
  },

  // Krankenkassen-Liste fuer Step3-Autocomplete. Endpoint existiert seit
  // crm-phase-0 als api.customer.getHealthInsurances. Idempotent — beim
  // ersten Aufruf gefuellt, danach Cache-Hit.
  fetchHealthInsurances: async () => {
    if (get().healthInsurances.length > 0) return;
    try {
      const healthInsurances = await api.customer.getHealthInsurances();
      set({ healthInsurances });
    } catch (error) {
      set({ error: (error as ErrorResponse).getResponseMessage?.() ?? 'Fehler beim Laden der Krankenkassen' });
    }
  },

  // `modusWahlOffen` faellt hier zu — ein gesetzter Modus und eine offene Wahl
  // schliessen sich aus. Das Wegschreiben in die Wizard-Sitzung erledigt seit
  // der Reload-Fortsetzung (07.08.2026) die Autosave-Anbindung unten; kein
  // Aufrufer speichert mehr selbst.
  setBookingType: (type) => set({ bookingType: type, modusWahlOffen: false }),

  // "Modus aendern" in Step 7: zurueck zur Wahl, OHNE die erfassten Daten
  // anzufassen. Bewusst nicht ueber resetWizard — der wirft den ganzen Vorgang
  // weg, und wer nur den Modus korrigieren will, hat seine Eingaben nicht zur
  // Disposition gestellt.
  oeffneModusWahl: () => set({ bookingType: null, modusWahlOffen: true }),

  /**
   * Einen gespeicherten Stand uebernehmen — der [Fortsetzen]-Zweig des
   * Dialogs.
   *
   * Ueber `initialState` gelegt und nicht ueber den aktuellen Stand: was NICHT
   * mitgespeichert wurde (Slots, `routedPool`, `bookingResult`, Fehler), muss
   * frisch sein und nicht die Reste dessen, was zufaellig im Store lag. Ein
   * `routedPool` aus einem anderen Vorgang waere genau die Art Beifahrer, aus
   * der Bug 1 (31.05.2026) entstanden ist.
   *
   * `practiceModeAvailable` bleibt stehen (Server-Flag, kein Wizard-Stand,
   * Bug-Fix 2026-06-20).
   */
  setzeWizardSitzungFort: (sitzung) => set((state) => ({
    ...initialState,
    practiceModeAvailable: state.practiceModeAvailable,
    healthInsurances: state.healthInsurances,
    ...sitzung.stand,
    // Stand aus der Zeit vor N1 (01.10.2026) traegt 5 = Förderrechner bzw.
    // 6 = Zusammenfassung — beides ist jetzt die Zusammenfassung.
    currentStep: klemmeWizardSchritt(sitzung.stand.currentStep),
  })),

  // Melder: der Wizard-Kalender legt beim Slot-Klick den angezeigten Kontext ab;
  // beim Entfernen einer weggefallenen Auswahl wird er auf null gesetzt.
  setSlotDisplayContext: (ctx) => set({ slotDisplayContext: ctx }),

  submitAndBook: async (scheduledAt: string) => {
    const state = get();
    const { wizardData, routedPool, foerderanspruch, bookingType } = state;

    // Riegel gegen den stillen Default: ohne gewaehlten Modus wird NICHT
    // gebucht. Vorher stand hier implizit 'pool' — genau der Weg, auf dem am
    // 07.08. eine gewollte Selbstbuchung als Pool-Buchung rausging (Meeting
    // 54415). Der Wizard laesst diesen Zustand gar nicht erst bis zum Kalender
    // durch; das hier ist der Gurt darunter.
    if (bookingType === null) {
      set({ modusWahlOffen: true, error: 'Bitte zuerst den Buchungsmodus wählen.' });
      return;
    }

    try {
      set({
        loading: true,
        error: null,
      });

      const recyclingCustomerId = state.recyclingCustomerId;
      const activeOpenCase = state.activeOpenCase;

      // Customer-Stammdaten kommen aus dem gemeinsamen Wizard-Builder.
      // Termin-/Slot-/Meeting-Felder werden nur hier obendrauf gelegt;
      // sie gehoeren NICHT in die saveDataOnly-Variante.
      const commonPayload = {
        ...buildWizardCustomerPayload(wizardData, foerderanspruch.total),
        scheduled_at: scheduledAt,
        title: `Erstberatung ${wizardData.vorname} ${wizardData.nachname}`,
        meeting_category: 'erstberatung',
        duration_minutes: 60,
        // ⛔ TOT SEIT DECISION #152 — UND SEIT #166 AUCH FOLGENLOS.
        //
        // `recyclingCustomerId` wird ausschliesslich von `startRecyclingBooking()`
        // gesetzt, das aus dem Feld `reactivation` der Kontaktsuche gespeist
        // wird. Dieses Feld befuellt der Server seit #152 nirgends mehr
        // (`CustomerController::contactSearch` kennt nur noch
        // `$payload['reactivation'] ??= null`) — der Wert ist hier also immer
        // `null` und `customer_id` geht nie mit.
        //
        // 🔥 GENAU DARAN IST DIE RC-BINDUNG GESTORBEN: der Server hing seine
        // RC-Erkennung an `$explicitCustomerId !== null`, das FE lieferte den
        // Wert nicht mehr, und jede Wiederbuchung eines RC-Kunden lief still
        // ueber den WRR — Fall reaktiviert, Berater falsch (42 gemessene
        // Faelle auf bwlive zwischen 13.08. und 18.08.).
        //
        // Mit Decision #166 (R4) entscheidet `customer_id` NICHTS mehr: die
        // Weiche haengt serverseitig am Customer-Match. Die Zeile bleibt
        // bewusst stehen — sie ist harmlos, der Server nimmt den Wert
        // weiterhin entgegen, und ein Rueckbau des Store-Pfads
        // (`startRecyclingBooking`, `activeRecycling`; das Banner ist seit
        // 30.10.2026 entfernt, der Pin bleibt)
        // ist ein eigener Aufraeum-Auftrag mit eigener Testlage.
        ...(recyclingCustomerId !== null ? { customer_id: recyclingCustomerId } : {}),
        // Pfad E — Buchung laeuft in den offenen Case beim Case-Owner.
        // case_direct schaltet BE-seitig den Offener-Case-Block ab und laesst das
        // Meeting pool-los speichern (zaehlt damit nicht ins VP-EB-Kontingent).
        ...(activeOpenCase !== null
          ? { customer_id: activeOpenCase.customer_id, case_direct: true }
          : {}),
        // Melder (16.07.2026): angezeigter Slot-Kontext beim Klick. Der BE-Endpoint
        // toleriert das Feld (validated() verwirft Unbekanntes) — kein BE-Umbau.
        // Dient der SLOT_TAKEN-Diagnose („angezeigter Stand" vs. Buchungs-Realitaet).
        ...(state.slotDisplayContext ? { display_context: state.slotDisplayContext } : {}),
        // Nachvollziehbarkeit (Auftrag Alex 07.08.2026): WAS die Oberflaeche
        // gewaehlt hat, geht mit — unabhaengig davon, welchen Endpunkt der
        // Aufruf danach nimmt. Am 07.08. liess sich fuer Meeting 54415 nur das
        // ERGEBNIS belegen (booking_origin=other) und nicht die Absicht; ob der
        // Agent "fuer mich" gewaehlt hatte, blieb Verdacht. Der Server
        // protokolliert das Feld neben dem aufgeloesten booking_origin, damit
        // eine Abweichung kuenftig in einer Zeile steht.
        booking_mode_selected: bookingType,
      };

      if (bookingType === 'self') {
        // Wiederholschutz, Variante C (Entscheid Alex 14.08.2026): UUID je
        // Absende-Absicht, ueber Fehlversuche hinweg stabil, erst nach dem
        // Erfolg beendet.
        const response = await api.callcenter.bookForMe({
          ...commonPayload,
          // Decision #238: der Pool, aus dem die angezeigten Slots stammen —
          // der Server prüft damit dessen Buchbare Zeiten (409 außerhalb).
          ...(routedPool ? { pool_id: routedPool.id } : {}),
          idempotency_key: absichtSchluessel('self'),
        });
        absichtAbschliessen('self');
        const rd = response.data as Record<string, unknown>;
        set({
          bookingResult: {
            customerId: 0,
            meetingId: response.data.meeting_id,
            assignedUserName: response.data.assigned_vp?.name ?? '',
            assignedUserAvatarUrl: response.data.assigned_vp?.avatar_url ?? null,
            assignedUserRole: response.data.assigned_vp?.role ?? 'vp',
            scheduledAt: response.data.scheduled_at ?? scheduledAt,
            contactName: response.data.contact_name ?? `${wizardData.vorname} ${wizardData.nachname}`,
            wasReactivated: (rd.was_reactivated as boolean) ?? false,
            reactivatedCaseId: (rd.reactivated_case_id as number | null) ?? null,
            reactivationCount: (rd.reactivation_count as number) ?? 0,
          },
        });
      } else {
        if (!routedPool) {
          set({ error: 'Pool noch nicht ermittelt — bitte erneut versuchen.' });
          return;
        }
        const bookingResponse = await api.callcenter.bookMeeting({
          ...commonPayload,
          pool_id: routedPool.id,
          // s.o. — eigener Kanal, eigener Schluessel.
          idempotency_key: absichtSchluessel('wizard'),
        });
        absichtAbschliessen('wizard');
        const rd = bookingResponse.data as Record<string, unknown>;
        set({
          bookingResult: {
            customerId: 0,
            meetingId: bookingResponse.data.meeting_id,
            assignedUserName: bookingResponse.data.assigned_vp?.name ?? '',
            assignedUserAvatarUrl: (bookingResponse.data.assigned_vp as Record<string, unknown>)?.avatar_url as string | null ?? null,
            assignedUserRole: (bookingResponse.data.assigned_vp as Record<string, unknown>)?.role as string ?? 'vp',
            scheduledAt,
            contactName: `${wizardData.vorname} ${wizardData.nachname}`,
            wasReactivated: (rd.was_reactivated as boolean) ?? false,
            reactivatedCaseId: (rd.reactivated_case_id as number | null) ?? null,
            reactivationCount: (rd.reactivation_count as number) ?? 0,
          },
        });
      }
    } catch (error) {
      const conflict = extractIntakeConflict(error);
      if (conflict) {
        set({ intakeConflict: conflict });
      } else {
        // SLOT_TAKEN-Härtung: bei Verfügbarkeits-Codes ein Refresh-Signal setzen
        // (Nonce hoch), das den Kalender die Slots neu laden + die Auswahl
        // zurücksetzen lässt. Kein Auto-Retry — der Agent wählt neu.
        const code = getBookingErrorCode(error);
        if (code && SLOT_REFRESH_CODES.has(code)) {
          set((s) => ({
            error: extractBookingError(error),
            slotRejection: { slot: scheduledAt, nonce: (s.slotRejection?.nonce ?? 0) + 1 },
          }));
        } else {
          set({ error: extractBookingError(error) });
        }
      }
    } finally {
      set({ loading: false });
    }
  },

  saveDataOnly: async () => {
    const state = get();
    const { wizardData, foerderanspruch } = state;

    try {
      set({ loading: true, error: null });

      // Wizard-spezifischer Pfad: deutsche Wizard-Slugs werden
      // serverseitig vom CustomerFieldMapper uebersetzt (siehe
      // /callcenter/save-customer). Generischer POST /customers
      // verlangt englische Enums und bleibt anderen Aufrufern
      // (CRM-Dialoge, externe Integration) vorbehalten.
      //
      // Customer-Stammdaten kommen aus dem gemeinsamen Wizard-Builder —
      // damit ist der Persistenz-Umfang automatisch identisch zum
      // Buchungs-Pfad (Bug B aus 2026-06-10-Diagnose).
      await api.callcenter.saveCustomerFromWizard({
        ...buildWizardCustomerPayload(wizardData, foerderanspruch.total),
        intake_source: 'wizard',
      });

      set({ dataSaved: true });
    } catch (error) {
      const conflict = extractIntakeConflict(error);
      if (conflict) {
        set({ intakeConflict: conflict });
      } else {
        set({ error: (error as ErrorResponse).getResponseMessage?.() ?? 'Fehler beim Speichern' });
      }
    } finally {
      set({ loading: false });
    }
  },

  setTestMode: (active: boolean) => set({ testMode: active, testRunSaved: false, error: null }),

  // Übungslauf: speichert den aktuellen Wizard-State + das Foerder-Ergebnis
  // im Sandbox-Endpoint (POST /api/wizard-sandbox/test-run). Es findet KEINE
  // echte Buchung statt — kein Customer, kein Case, kein Meeting, keine
  // Outbox, kein externer Service. Bewusst getrennt von submitAndBook/
  // saveDataOnly, die unveraendert bleiben.
  saveTestRun: async () => {
    const state = get();
    const { wizardData, foerderanspruch } = state;

    try {
      set({ loading: true, error: null });

      await api.wizardSandbox.saveTestRun({
        wizard_payload: wizardData,
        foerder_result: foerderanspruch,
      });

      set({ testRunSaved: true });
    } catch (error) {
      set({ error: (error as ErrorResponse).getResponseMessage?.() ?? 'Fehler beim Speichern des Übungslaufs' });
    } finally {
      set({ loading: false });
    }
  },

  // Serverseitigen Übungsmodus-Status laden (Feature-Flag). Steuert die
  // Sichtbarkeit des Übungsmodus-Buttons. Bei Fehler bleibt der Modus sicher
  // verborgen (false) — kein Aufreissen des Buttons bei unklarem Backend.
  fetchPracticeModeStatus: async () => {
    try {
      const { enabled } = await api.wizardSandbox.getStatus();
      set({ practiceModeAvailable: enabled });
    } catch {
      set({ practiceModeAvailable: false });
    }
  },

  dismissIntakeConflict: () => set({ intakeConflict: null }),

  // practiceModeAvailable ist ein Feature-Flag (Server-Status), kein
  // Wizard-State — wird vom Reset bewusst nicht angefasst (Bug-Fix 2026-06-20).
  //
  // Der gespeicherte Stand wird hier VERGESSEN: ein neuer Durchlauf ist ein
  // neuer Vorgang. Das gilt auch fuer den Logout — `useClearState` raeumt den
  // callcenter-Store genau ueber diese Methode (Logout-Hygiene aus dem
  // Bug-1-Fix vom 31.05.2026), und damit faellt die Sitzung dort mit.
  resetWizard: () => {
    vergissWizardSitzung();
    set((state) => ({ ...initialState, practiceModeAvailable: state.practiceModeAvailable }));
  },
}));

/**
 * AUTOSAVE — eine Anbindung statt eines Aufrufs je Aktion.
 *
 * Jede Store-Aenderung schreibt den Stand in die Wizard-Sitzung, sobald er es
 * wert ist (`istSpeicherwuerdig`), und raeumt ihn sonst weg. Das deckt auch
 * die Wege ab, die niemand auf dem Zettel hat — ein neues Feld in Step 3, ein
 * kuenftiger Sprung-Pfad —, ohne dass jemand daran denken muss.
 *
 * Die User-ID kommt aus dem auth-Store und wird MITGESCHRIEBEN, nicht nur beim
 * Lesen geprueft: nur so laesst sich beim Laden feststellen, wem der Stand
 * gehoert. Der Import ist gerichtet (callcenter → auth), der auth-Store kennt
 * den callcenter-Store nicht — kein Zyklus.
 *
 * `getState()` statt eines Hooks, weil das hier ausserhalb von React laeuft.
 */
useCallcenterStore.subscribe((state) => {
  if (istSpeicherwuerdig(state)) {
    speichereWizardSitzung(state, useAuthStore.getState().user?.id ?? null);
  } else {
    vergissWizardSitzung();
  }
});

/**
 * Gespeicherten Stand lesen, ohne ihn einzuspielen.
 *
 * Getrennt vom Uebernehmen, und das ist der Kern des Entscheids: Bug 1
 * (31.05.2026) war ein Zustand, der STILL weiterlief. Der Wizard fragt mit
 * diesem Ergebnis erst; eingespielt wird ausschliesslich auf Klick
 * (`setzeWizardSitzungFort`).
 */
export function lieferWizardSitzung(): WizardSitzung | null {
  return ladeWizardSitzung(useAuthStore.getState().user?.id ?? null);
}

export { vergissWizardSitzung };

export default useCallcenterStore;
